import {
  alpha,
  Badge,
  Box,
  Card,
  Chip,
  Container,
  Divider,
  Fab,
  IconButton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  Add,
  CalendarMonth,
  Delete,
  Edit,
  TableChart,
  Close,
  CheckCircleOutline,
  CancelOutlined,
} from "@mui/icons-material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as React from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState } from "store";
import { AuthState } from "store/features/auth";
import {
  getChildren as getChildrenApi,
  getAttendances as getAttendanceApi,
  updateAttendance as updateAttendanceApi,
} from "apis";
import { useSnackbar } from "notistack";
import { IChild } from "types";
import AddAttendanceDialog from "components/AddAttendanceDialog";
import DeleteAttendenceDialog from "components/DeleteAttendenceDialog";
import EditAttendanceDialog from "components/EditAttendanceDialog";
import * as locales from "@mui/material/locale";
import "dayjs/locale/ar";
import "dayjs/locale/fr";
import updateLocale from "dayjs/plugin/updateLocale";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Calendar, dayjsLocalizer, Messages } from "react-big-calendar";
import { isRoomLeaderOrAdmin } from "utils/roles";

dayjs.extend(updateLocale);
dayjs.updateLocale("ar", {
  weekdaysShort: "الأحد_الإثنين_الثلاثاء_الأربعاء_الخميس_الجمعة_السبت".split(
    "_",
  ),
});

const localizer = dayjsLocalizer(dayjs);

type SupportedLocales = keyof typeof locales;

type Attendance = {
  id: number;
  child: IChild;
  type: number;
  reason: string;
  comment: string;
  startDate: string;
  endDate: string;
  time: string;
  status: number;
};

type SortKey = "child" | "type" | "date" | "status";
type SortDir = "asc" | "desc";

const statusConfig = (status: number, t: (key: string) => string) => {
  if (status === 0)
    return { label: t("labels.pending"), color: "warning" as const };
  if (status === 1)
    return { label: t("labels.approved"), color: "success" as const };
  return { label: t("labels.declined"), color: "error" as const };
};

// Backend stores one of three named buckets after the
// AttendanceTimeEnum1779100000000 migration.
const timeLabel = (time: string, t: (key: string) => string) => {
  if (time === "morning") return t("labels.morning");
  if (time === "afternoon") return t("labels.afternoon");
  return t("labels.whole_day");
};

const TYPE_FILTERS = [
  { value: "all", labelKey: "posts.all" },
  { value: "absence", labelKey: "labels.absence" },
  { value: "extra", labelKey: "labels.extra_session" },
];

const STATUS_FILTERS = [
  { value: "all", labelKey: "posts.all" },
  { value: "pending", labelKey: "labels.pending", statusCode: 0 },
  { value: "approved", labelKey: "labels.approved", statusCode: 1 },
  { value: "declined", labelKey: "labels.declined", statusCode: 2 },
];

const AttendancePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const authInfo = useSelector<RootState, AuthState>((store) => store.auth);

  const [tabIndex, setTabIndex] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [attendances, setAttendances] = useState<Attendance[]>([]);
  const [children, setChildren] = useState<IChild[]>([]);
  const [loading, setLoading] = useState(true);

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editItem, setEditItem] = useState<Attendance>();
  const [deleteId, setDeleteId] = useState<number>();

  // Sorting
  const [sortKey, setSortKey] = useState<SortKey>("date");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // Filters
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [locale, setLocale] = useState<SupportedLocales>("enUS");

  const themeWithLocale = useMemo(
    () => createTheme(theme, locales[locale]),
    [locale, theme],
  );

  useEffect(() => {
    const map: Record<string, SupportedLocales> = {
      en: "enUS",
      fr: "frFR",
      ar: "arSA",
    };
    setLocale(map[i18n.language] ?? "enUS");
  }, [i18n.language]);

  const fetchData = useCallback(() => {
    setLoading(true);
    Promise.all([getAttendanceApi(), getChildrenApi()])
      .then(([att, ch]) => {
        setAttendances(att || []);
        setChildren(ch || []);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDialogClose = useCallback(
    (type: "add" | "edit" | "delete") => {
      if (type === "add") setOpenAddDialog(false);
      if (type === "edit") setOpenEditDialog(false);
      if (type === "delete") setOpenDeleteDialog(false);
      fetchData();
    },
    [fetchData],
  );

  const { enqueueSnackbar } = useSnackbar();

  // Inline approve/decline. Sends the existing attendance fields back
  // with the new status — server preserves childId regardless and only
  // the role-allowed mutations land.
  const handleStatusChange = useCallback(
    (attendance: Attendance, newStatus: number) => {
      updateAttendanceApi({
        infos: {
          id: attendance.id,
          child_id: Number(attendance.child.id),
          attendance: attendance.type,
          time: attendance.time as "morning" | "afternoon" | "whole_day",
          reason: attendance.reason,
          comment: attendance.comment,
          status: newStatus,
        },
        startDate: dayjs(attendance.startDate),
        endDate: dayjs(attendance.endDate),
      })
        .then(() => {
          enqueueSnackbar(
            t(
              newStatus === 1 ? "attendance.approved" : "attendance.declined",
            ) || (newStatus === 1 ? "Approved" : "Declined"),
            { variant: "success" },
          );
          fetchData();
        })
        .catch((err) => {
          enqueueSnackbar(err?.response?.data?.message || "Failed", {
            variant: "error",
          });
        });
    },
    [enqueueSnackbar, fetchData, t],
  );

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const filteredAttendances = useMemo(() => {
    let result = attendances;

    if (typeFilter !== "all") {
      result = result.filter((a) =>
        typeFilter === "absence" ? a.type === 0 : a.type !== 0,
      );
    }

    if (statusFilter !== "all") {
      const code = STATUS_FILTERS.find(
        (f) => f.value === statusFilter,
      )?.statusCode;
      if (code !== undefined) {
        result = result.filter((a) => a.status === code);
      }
    }

    return result;
  }, [attendances, typeFilter, statusFilter]);

  const sortedAttendances = useMemo(() => {
    const sorted = filteredAttendances.slice();
    const dir = sortDir === "asc" ? 1 : -1;

    sorted.sort((a, b) => {
      switch (sortKey) {
        case "child":
          return (
            dir *
            `${a.child.firstName} ${a.child.lastName}`.localeCompare(
              `${b.child.firstName} ${b.child.lastName}`,
            )
          );
        case "type":
          return dir * (a.type - b.type);
        case "date":
          return (
            dir *
            (new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
          );
        case "status":
          return dir * (a.status - b.status);
        default:
          return 0;
      }
    });

    return sorted;
  }, [filteredAttendances, sortKey, sortDir]);

  const activeFilterCount =
    (typeFilter !== "all" ? 1 : 0) + (statusFilter !== "all" ? 1 : 0);

  const calendarEvents = useMemo(() => {
    return attendances
      .filter((a) => a.status < 2)
      .map((a) => {
        const type = a.type ? t("labels.extra_session") : t("labels.absence");
        const time = timeLabel(a.time, t);
        const reason =
          !a.type && a.reason ? ` · ${t(`labels.${a.reason}`)}` : "";
        return {
          title: `${a.child.firstName} ${a.child.lastName} — ${type} (${time})${reason}`,
          start: new Date(a.startDate),
          end: new Date(a.endDate),
          colorEvento: a.type
            ? theme.palette.primary.main
            : theme.palette.error.main,
          color: "white",
        };
      });
  }, [attendances, t, theme]);

  const calendarMessages: Messages = useMemo(
    () => ({
      today: t("calendar.today"),
      previous: t("calendar.previous"),
      next: t("calendar.next"),
      month: t("calendar.month"),
      week: t("calendar.week"),
      day: t("calendar.day"),
      agenda: t("calendar.agenda"),
      date: t("labels.date"),
      time: t("labels.time"),
      event: t("inputs.events"),
      noEventsInRange: t("calendar.no_events"),
      showMore: (total) => t("calendar.show_more", { count: total }),
    }),
    [t],
  );

  const isParent = authInfo.role === "PARENT";

  return (
    <>
      {/* Tab bar */}
      <Toolbar
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          justifyContent: "center",
        }}
      >
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              minHeight: 48,
            },
          }}
        >
          <Tab
            icon={<TableChart sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={t("attendance.label")}
          />
          <Tab
            icon={<CalendarMonth sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label={t("labels.calendar")}
          />
        </Tabs>
      </Toolbar>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Table view */}
          {tabIndex === 0 && (
            <Container sx={{ py: 2 }}>
              {/* Filter bar */}
              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { sm: "center" },
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                {/* Type filter group */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    bgcolor: alpha(theme.palette.text.primary, 0.03),
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.75,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                    sx={{
                      fontSize: 10,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      mr: 0.5,
                    }}
                  >
                    {t("inputs.type")}
                  </Typography>
                  {[
                    {
                      value: "all",
                      label: t("posts.all"),
                      color: "primary" as const,
                    },
                    {
                      value: "absence",
                      label: t("labels.absence"),
                      color: "error" as const,
                    },
                    {
                      value: "extra",
                      label: t("labels.extra_session"),
                      color: "primary" as const,
                    },
                  ].map(({ value, label, color }) => (
                    <Chip
                      key={value}
                      label={label}
                      size="small"
                      variant={typeFilter === value ? "filled" : "outlined"}
                      color={typeFilter === value ? color : "default"}
                      onClick={() => {
                        setTypeFilter(value);
                        setPage(0);
                      }}
                      sx={{
                        fontWeight: typeFilter === value ? 600 : 400,
                        cursor: "pointer",
                        transition: "all 0.15s",
                        borderColor:
                          typeFilter === value ? undefined : "transparent",
                      }}
                    />
                  ))}
                </Box>

                {/* Status filter group */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    bgcolor: alpha(theme.palette.text.primary, 0.03),
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.75,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={700}
                    sx={{
                      fontSize: 10,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      mr: 0.5,
                    }}
                  >
                    {t("labels.status")}
                  </Typography>
                  {[
                    {
                      value: "all",
                      label: t("posts.all"),
                      color: "primary" as const,
                    },
                    {
                      value: "approved",
                      label: t("labels.approved"),
                      color: "success" as const,
                    },
                    {
                      value: "pending",
                      label: t("labels.pending"),
                      color: "warning" as const,
                    },
                    {
                      value: "declined",
                      label: t("labels.declined"),
                      color: "error" as const,
                    },
                  ].map(({ value, label, color }) => (
                    <Chip
                      key={value}
                      label={label}
                      size="small"
                      variant={statusFilter === value ? "filled" : "outlined"}
                      color={statusFilter === value ? color : "default"}
                      onClick={() => {
                        setStatusFilter(value);
                        setPage(0);
                      }}
                      sx={{
                        fontWeight: statusFilter === value ? 600 : 400,
                        cursor: "pointer",
                        transition: "all 0.15s",
                        borderColor:
                          statusFilter === value ? undefined : "transparent",
                      }}
                    />
                  ))}
                </Box>

                {/* Count + clear */}
                <Box
                  sx={{
                    ml: { sm: "auto" },
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {activeFilterCount > 0 && (
                    <Chip
                      icon={<Close sx={{ fontSize: 14 }} />}
                      label={t("inputs.clear")}
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setTypeFilter("all");
                        setStatusFilter("all");
                        setPage(0);
                      }}
                      sx={{ cursor: "pointer" }}
                    />
                  )}
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    fontWeight={600}
                  >
                    {sortedAttendances.length}{" "}
                    {sortedAttendances.length === 1
                      ? t("finance.record")
                      : t("finance.records")}
                  </Typography>
                </Box>
              </Box>

              <Card
                variant="outlined"
                sx={{ borderRadius: 3, overflow: "hidden" }}
              >
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.04),
                        }}
                      >
                        <TableCell sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={sortKey === "child"}
                            direction={sortKey === "child" ? sortDir : "asc"}
                            onClick={() => handleSort("child")}
                          >
                            {t("timeline.child")}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={sortKey === "type"}
                            direction={sortKey === "type" ? sortDir : "asc"}
                            onClick={() => handleSort("type")}
                          >
                            {t("inputs.type")}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={sortKey === "date"}
                            direction={sortKey === "date" ? sortDir : "asc"}
                            onClick={() => handleSort("date")}
                          >
                            {t("labels.date")}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {t("labels.time")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {t("inputs.comment")}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          <TableSortLabel
                            active={sortKey === "status"}
                            direction={sortKey === "status" ? sortDir : "asc"}
                            onClick={() => handleSort("status")}
                          >
                            {t("labels.status")}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="center">
                          {t("labels.action")}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedAttendances.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                            <Typography color="text.secondary">
                              {activeFilterCount > 0
                                ? t("attendance.no_match")
                                : t("attendance.empty")}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        sortedAttendances
                          .slice(
                            page * rowsPerPage,
                            page * rowsPerPage + rowsPerPage,
                          )
                          .map((attendance) => {
                            const status = statusConfig(attendance.status, t);
                            const canEdit =
                              isRoomLeaderOrAdmin(authInfo.role) ||
                              (isParent && attendance.status === 0);
                            const canApproveDecline =
                              isRoomLeaderOrAdmin(authInfo.role) &&
                              attendance.status === 0;
                            return (
                              <TableRow key={attendance.id} hover>
                                <TableCell>
                                  <Typography variant="body2" fontWeight={500}>
                                    {attendance.child.firstName}{" "}
                                    {attendance.child.lastName}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {attendance.child.classroom?.name}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={
                                      attendance.type
                                        ? t("labels.extra_session")
                                        : t("labels.absence")
                                    }
                                    color={
                                      attendance.type ? "primary" : "error"
                                    }
                                    variant="outlined"
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {dayjs(attendance.startDate).format(
                                      "DD MMM",
                                    )}{" "}
                                    —{" "}
                                    {dayjs(attendance.endDate).format(
                                      "DD MMM YYYY",
                                    )}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {timeLabel(attendance.time, t)}
                                  </Typography>
                                  {!attendance.type && attendance.reason && (
                                    <Typography
                                      variant="caption"
                                      color="error.main"
                                    >
                                      {t(`labels.${attendance.reason}`)}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell sx={{ maxWidth: 200 }}>
                                  <Typography
                                    variant="body2"
                                    noWrap
                                    title={attendance.comment}
                                  >
                                    {attendance.comment || "—"}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={status.label}
                                    color={status.color}
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  {canApproveDecline && (
                                    <>
                                      <Tooltip title={t("labels.approved")}>
                                        <IconButton
                                          size="small"
                                          color="success"
                                          onClick={() =>
                                            handleStatusChange(attendance, 1)
                                          }
                                        >
                                          <CheckCircleOutline fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title={t("labels.declined")}>
                                        <IconButton
                                          size="small"
                                          color="warning"
                                          onClick={() =>
                                            handleStatusChange(attendance, 2)
                                          }
                                        >
                                          <CancelOutlined fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  )}
                                  {canEdit && (
                                    <>
                                      <Tooltip title={t("labels.edit")}>
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            setEditItem(attendance);
                                            setOpenEditDialog(true);
                                          }}
                                        >
                                          <Edit fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title={t("buttons.delete")}>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => {
                                            setDeleteId(attendance.id);
                                            setOpenDeleteDialog(true);
                                          }}
                                        >
                                          <Delete fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Divider />
                <ThemeProvider theme={themeWithLocale}>
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={sortedAttendances.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                  />
                </ThemeProvider>
              </Card>
            </Container>
          )}

          {/* Calendar view */}
          {tabIndex === 1 && (
            <Container sx={{ py: 2 }}>
              <Card variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                <Calendar
                  defaultDate={new Date()}
                  localizer={localizer}
                  culture={i18n.language}
                  messages={calendarMessages}
                  events={calendarEvents}
                  showMultiDayTimes
                  step={60}
                  startAccessor="start"
                  endAccessor="end"
                  popup
                  rtl={i18n.dir(i18n.language) === "rtl"}
                  style={{ height: "min(700px, calc(100vh - 200px))" }}
                  views={{ month: true }}
                  eventPropGetter={(event) => ({
                    style: {
                      backgroundColor: event.colorEvento,
                      color: event.color,
                      borderRadius: 6,
                      border: "none",
                      fontSize: 12,
                    },
                  })}
                />
              </Card>
            </Container>
          )}
        </>
      )}

      {/* Add FAB */}
      {isParent && (
        <Fab
          variant="extended"
          color="primary"
          sx={{
            position: "fixed",
            right: 24,
            bottom: 24,
            pl: 2.5,
            pr: 3,
            gap: 1,
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 3,
            boxShadow: "0 4px 14px 0 rgba(0,0,0,0.25)",
            "&:hover": { boxShadow: "0 6px 20px 0 rgba(0,0,0,0.3)" },
          }}
          onClick={() => setOpenAddDialog(true)}
        >
          <Add />
          {t("attendance.add")}
        </Fab>
      )}

      {/* Dialogs */}
      {openAddDialog && (
        <AddAttendanceDialog
          open={openAddDialog}
          onClose={() => handleDialogClose("add")}
          child={children}
        />
      )}
      {openEditDialog && editItem && (
        <EditAttendanceDialog
          open={openEditDialog}
          onClose={() => handleDialogClose("edit")}
          item={editItem}
          child={children}
        />
      )}
      {openDeleteDialog && deleteId !== undefined && (
        <DeleteAttendenceDialog
          open={openDeleteDialog}
          onClose={() => handleDialogClose("delete")}
          id={deleteId}
        />
      )}
    </>
  );
};

export default AttendancePage;
